import { FC, useMemo } from 'react';
import {
  Box,
  Flex,
  Stack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react';
import { Paginate } from '../../../appPaginate';
import moment from 'moment';
import type { ColumnDef } from '@tanstack/react-table';
import { NewDataGrid } from '../../../NewGridTable/NewDataGrid';
import { MetricModalLoading } from './DefaultDetailsModal/MetricModalLoading';

interface QualityDetailItem {
  analysisId: string;
  project: string;
  branch: string;
  date: string;
  value: string | null;
  expectedValue: string | null;
}

interface QualityDetailData {
  data: QualityDetailItem[];
  currentPage?: number;
  totalPages?: number;
}

interface QualityDetailsModalProps {
  openDrawer: boolean;
  handleDrawerClose: () => void;
  qualityData?: QualityDetailData;
  heading: string;
  subHeading: string;
  isFetching: boolean;
  handlePageClick: (page: number) => void;
}

const QualityDetailsModal: FC<QualityDetailsModalProps> = ({
  openDrawer,
  handleDrawerClose,
  qualityData,
  heading,
  subHeading,
  isFetching,
  handlePageClick,
}) => {
  const rows: QualityDetailItem[] = useMemo(
    () => qualityData?.data || [],
    [qualityData?.data]
  );

  const columns: ColumnDef<QualityDetailItem, any>[] = useMemo(
    () => [
      {
        header: 'Date',
        accessorKey: 'date',
        enableSorting: false,
        size: 200,
        cell: ({ row }) => {
          const value = row.original?.date;
          if (!value) {
            return '-';
          }

          const formatted = moment(value).format('DD MMM YYYY, h:mm A');

          return (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
              whiteSpace="nowrap"
            >
              {formatted}
            </Text>
          );
        },
      },
      {
        header: 'Project',
        accessorKey: 'project',
        enableSorting: false,
        size: 220,
        cell: ({ row }) => {
          const value = row.original?.project;
          return value ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.primary"
              fontWeight="medium"
              noOfLines={1}
            >
              {value}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Branch',
        accessorKey: 'branch',
        enableSorting: false,
        size: 200,
        cell: ({ row }) => {
          const value = row.original?.branch;
          return value ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {value}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Expected Value',
        accessorKey: 'expectedValue',
        enableSorting: false,
        size: 180,
        cell: ({ row }) => {
          const value = row.original?.expectedValue;
          return value ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {value}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Value',
        accessorKey: 'value',
        enableSorting: false,
        size: 140,
        cell: ({ row }) => {
          const value = row.original?.value;
          return value ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {value}
            </Text>
          ) : (
            '-'
          );
        },
      },
      {
        header: 'Analysis Id',
        accessorKey: 'analysisId',
        enableSorting: false,
        size: 220,
        cell: ({ row }) => {
          const value = row.original?.analysisId;
          return value ? (
            <Text
              fontSize="sm"
              fontFamily="heading"
              color="text.secondary"
              fontWeight="medium"
              noOfLines={1}
            >
              {value}
            </Text>
          ) : (
            '-'
          );
        },
      },
    ],
    []
  );

  return (
    <Modal
      isOpen={openDrawer}
      onClose={handleDrawerClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      autoFocus={false}
    >
      <ModalOverlay />
      <ModalContent minW="4xl" maxW="4xl">
        <ModalHeader pb={2}>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Stack spacing={1}>
              <Text
                fontFamily="heading"
                fontSize="lg"
                fontWeight="bold"
                color="text.primary"
              >
                {heading}
              </Text>
              <Text
                fontFamily="heading"
                fontSize="sm"
                color="text.secondary"
                fontWeight="medium"
              >
                {subHeading}
              </Text>
            </Stack>
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />
        <ModalBody pb={6}>
          <Stack spacing={4}>
            {isFetching && !qualityData?.data ? (
              <MetricModalLoading />
            ) : rows && rows.length > 0 ? (
              <>
                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  overflow="hidden"
                  w="100%"
                >
                  <NewDataGrid
                    data={rows}
                    columns={columns}
                    showLoader={false}
                    skeletonLoader={false}
                    sticky="none"
                    useMaxH
                    showGridLines
                  />
                </Box>

                {qualityData?.data?.length &&
                  (qualityData.currentPage ?? 0) + 1 &&
                  qualityData.totalPages !== undefined &&
                  qualityData.totalPages > 1 && (
                    <Flex justify="center" mt={2}>
                      <Paginate
                        currentPage={(qualityData.currentPage ?? 0) + 1}
                        totalPageCount={qualityData.totalPages ?? 0}
                        pageSize={20}
                        onPageChange={(page) => handlePageClick(page as number)}
                      />
                    </Flex>
                  )}
              </>
            ) : (
              <Box
                borderWidth="1px"
                borderRadius="md"
                overflow="hidden"
                w="100%"
                minH="200px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontFamily="heading"
                  fontSize="sm"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  No data found
                </Text>
              </Box>
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QualityDetailsModal;
